import React, { createContext, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { Modal, ModalFooter } from './Modal';
import { Button } from './Button';
import { Input, Label } from './Form';

export interface ConfirmOptions {
  title?: React.ReactNode;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' colora il bottone di conferma in rosso (azioni distruttive). */
  variant?: 'default' | 'danger';
  /**
   * Se valorizzato, l'utente deve digitare esattamente questo testo per
   * abilitare la conferma (per azioni irreversibili, es. reset database).
   */
  requireText?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

const MessageBody = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: flex-start;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;

  p {
    margin: 0 0 var(--space-sm);

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const WarnIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: var(--error-soft);
  color: var(--error);
  flex-shrink: 0;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const RequireBox = styled.div`
  margin-top: var(--space-md);

  code {
    background: var(--gray-100);
    padding: 0.125rem 0.375rem;
    border-radius: var(--border-radius-sm);
    font-family: var(--font-family-mono);
    font-size: var(--font-size-xs);
  }
`;

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

/**
 * Provider del dialogo di conferma. Sostituisce window.confirm/prompt:
 *   const confirm = useConfirm();
 *   if (await confirm({ message: 'Eliminare il turno?', variant: 'danger' })) { ... }
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [typedText, setTypedText] = useState('');

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setTypedText('');
      setPending({ options, resolve });
    });
  }, []);

  const close = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
    setTypedText('');
  };

  const options = pending?.options;
  const isDanger = options?.variant === 'danger';
  const requireOk = !options?.requireText || typedText === options.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <Modal
          open
          onClose={() => close(false)}
          title={options.title ?? 'Conferma'}
          closeOnOverlay={false}
          footer={
            <ModalFooter as="div" style={{ border: 'none', padding: 0 }}>
              <Button $variant="secondary" onClick={() => close(false)}>
                {options.cancelLabel ?? 'Annulla'}
              </Button>
              <Button
                $variant={isDanger ? 'danger' : 'primary'}
                disabled={!requireOk}
                onClick={() => close(true)}
              >
                {options.confirmLabel ?? 'Conferma'}
              </Button>
            </ModalFooter>
          }
        >
          <MessageBody>
            {isDanger && (
              <WarnIcon>
                <AlertTriangle />
              </WarnIcon>
            )}
            <div>
              {options.message}
              {options.requireText && (
                <RequireBox>
                  <Label htmlFor="confirm-require-text">
                    Per procedere digita <code>{options.requireText}</code>
                  </Label>
                  <Input
                    id="confirm-require-text"
                    value={typedText}
                    onChange={(e) => setTypedText(e.target.value)}
                    autoFocus
                    style={{ width: '100%' }}
                  />
                </RequireBox>
              )}
            </div>
          </MessageBody>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm deve essere usato dentro <ConfirmProvider>');
  }
  return ctx;
}
